/**
 * Hook per lo storico WhatsApp di un cliente nella cronologia (vista 7, BETA).
 *
 * Compone `useWhatsAppConfigs` (config filtrate per-utente via
 * `whatsapp_user_access`: la card compare SOLO a chi ha accesso a quella chat)
 * con `fetchClientWhatsAppSessions`. Tutti gli errori sono soft: stato `error`,
 * mai throw — la cronologia note/attività non deve mai rompersi per WhatsApp.
 *
 * `clientId` null disattiva l'hook (nessun fetch: partner, viste ridotte).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserData } from '../auth';
import { useWhatsAppConfigs } from './useWhatsAppConfigs';
import {
  fetchClientWhatsAppSessions,
  type ClientWhatsAppSession,
} from '../services/clientWhatsAppService';

export function useClientWhatsAppSessions(clientId: string | null, dealId?: string | null) {
  const { organizationId } = useUserData();
  const { configs, loading: configsLoading } = useWhatsAppConfigs();
  const [sessions, setSessions] = useState<ClientWhatsAppSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    // Config ancora in caricamento: aspetta il loro effect (deps su configs).
    if (!clientId || !organizationId || configsLoading) return;
    const requestId = ++requestIdRef.current;
    if (configs.length === 0) {
      setSessions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const result = await fetchClientWhatsAppSessions({
        clientId,
        organizationId,
        configs: configs.map((c) => ({
          id: c.id,
          label: c.displayName || c.phoneNumber || 'WhatsApp',
        })),
        dealId: dealId ?? null,
      });
      if (requestIdRef.current !== requestId) return; // stale
      setSessions(result);
    } catch (err) {
      console.error('[useClientWhatsAppSessions]', err);
      if (requestIdRef.current === requestId) setError(true);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [clientId, dealId, organizationId, configs, configsLoading]);

  useEffect(() => {
    requestIdRef.current += 1; // scarta le risposte in volo del cliente precedente
    setSessions([]);
    setError(false);
    void load();
  }, [load]);

  return { sessions, loading, error, refresh: load };
}
